import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { baseModulesRoutes } from '@app/config';
import { IAccessTokenData } from '@interfaces/auth.interface';
import { UsersService } from '@services/api/administracja/user-access/users.service';
import { AuthService } from '@services/auth.service';
import { BehaviorSubject, combineLatest, filter } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DefaultModuleService {
  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly permissionsService: UsersService,
  ) {}

  handleRouterEvents(): void {
    const routerNavigationEndEvent$ = this.router.events.pipe(filter((val) => val instanceof NavigationEnd));
    const permissions$ = this.permissionsService.permissions$.pipe(filter(Boolean));
    const token$ = this.authService.authTokenData$.pipe(filter(Boolean));

    combineLatest([routerNavigationEndEvent$, permissions$, token$ as BehaviorSubject<IAccessTokenData>]).subscribe(
      ([_navigationEvent, _permissions, token]) => {
        const { url } = this.router;
        if (token.role) {
          if (token.role.includes('Admin') || token.role.includes('Pracownik')) {
            this.handleAdminRedirection(url);
          }
          if (token.role.includes('Wykładowca')) {
            this.handleLecturerRedirection(url);
          }
          if (token.role.includes('Student')) {
            this.handleStudentRedirection(url);
          }
        }
      },
    );
  }

  private handleAdminRedirection(url: string): void {
    if (
      url === baseModulesRoutes.administration &&
      this.permissionsService.doesUserHavePermission('KonfiguracjaUczelni.PlatformaAdministracyjna.Osoby.Lista')
    ) {
      this.router.navigateByUrl('/administracja/osoby', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.studies &&
      this.permissionsService.doesUserHavePermission('Studia.PlatformaAdministracyjna.Studenci.Lista')
    ) {
      this.router.navigateByUrl('/studia/admin/studenci', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.dissertation &&
      this.permissionsService.doesUserHavePermission('Dyplomowanie.PracownikAdministracyjny.PraceDyplomowe.Podglad')
    ) {
      this.router.navigateByUrl('/dyplomowanie/admin/prace-dyplomowe', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.practices &&
      this.permissionsService.doesUserHavePermission('Praktyki.PracownikAdministracyjny.PraktykiStudentow.Lista')
    ) {
      this.router.navigateByUrl('/praktyki/administrator/praktyki-studentow', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.requests &&
      this.permissionsService.doesUserHavePermission(
        'Wnioski.PlatformaAdministracyjna.SprawyStudenta.PrzegladanieListy',
      )
    ) {
      this.router.navigateByUrl('/wnioski/admin/sprawy-studentow', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.scholarships &&
      this.permissionsService.doesUserHavePermission(
        'Stypendia.PlatformaAdministracyjna.SprawyStudenta.PrzegladanieListy',
      )
    ) {
      this.router.navigateByUrl('/stypendia/admin/postepowania-studentow', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.finances &&
      this.permissionsService.doesUserHavePermission(
        'Finanse.PracownikAdministracyjny.ProgramStudiowStudentaDaneFinansowe.Lista',
      )
    ) {
      this.router.navigateByUrl('/finanse/administrator/studenci', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.classifieds &&
      this.permissionsService.doesUserHavePermission('Komunikacja.PlatformaAdministracyjna.Ogloszenia.Lista')
    ) {
      this.router.navigateByUrl('/ogloszenia/administrator', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.filesRepository &&
      this.permissionsService.doesUserHavePermission(
        'RepozytoriumPlikow.PlatformaAdministracyjna.ZarzadzaniePlikami.Lista',
      )
    ) {
      this.router.navigateByUrl('/repozytorium-plikow/admin/pliki-do-pobrania', { replaceUrl: true });
    }
  }

  private handleStudentRedirection(url: string): void {
    if (
      url === baseModulesRoutes.studies &&
      this.permissionsService.doesUserHavePermission('Studia.PlatformaStudenta.Przedmioty.Podglad')
    ) {
      this.router.navigateByUrl('/studia/student/moje-oceny', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.dissertation &&
      this.permissionsService.doesUserHavePermission('Dyplomowanie.Student.EgzaminyDyplomowe.PodgladListy')
    ) {
      this.router.navigateByUrl('/dyplomowanie/student/moje-egzaminy', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.practices &&
      this.permissionsService.doesUserHavePermission('Praktyki.Student.PraktykiStudentow.Lista')
    ) {
      this.router.navigateByUrl('/praktyki/student/moje-praktyki', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.requests &&
      this.permissionsService.doesUserHavePermission('Wnioski.PlatformaStudenta.PodaniaStudenta.PrzegladanieListy')
    ) {
      this.router.navigateByUrl('/wnioski/student/moje-podania', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.scholarships &&
      this.permissionsService.doesUserHavePermission('Stypendia.PlatformaStudenta.PodstawaWniosku.PrzegladanieListy')
    ) {
      this.router.navigateByUrl('/stypendia/student/moje-wnioski', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.classifieds &&
      this.permissionsService.doesUserHavePermission('Komunikacja.PlatformaStudenta.Ogloszenia.Lista')
    ) {
      this.router.navigateByUrl('/ogloszenia/student', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.finances &&
      this.permissionsService.doesUserHavePermission('Finanse.Student.Oplaty.Lista')
    ) {
      this.router.navigateByUrl('/finanse/student/oplaty-i-wplaty', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.filesRepository &&
      this.permissionsService.doesUserHavePermission('RepozytoriumPlikow.Student.Pliki.Lista')
    ) {
      this.router.navigateByUrl('/repozytorium-plikow/student/pliki-do-pobrania', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.mojeDane &&
      this.permissionsService.doesUserHavePermission('Studia.PlatformaStudenta.Osoby.DaneOsobowe')
    ) {
      this.router.navigateByUrl('/moje-dane/student/dane-osobowe', { replaceUrl: true });
    }
  }

  private handleLecturerRedirection(url: string): void {
    if (
      url === baseModulesRoutes.studies &&
      this.permissionsService.doesUserHavePermission('Studia.PlatformaPracownikaNaukowego.TerminyZajec.Podglad')
    ) {
      this.router.navigateByUrl('/studia/wykladowca/moje-terminy-zajec', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.dissertation &&
      this.permissionsService.doesUserHavePermission('Dyplomowanie.PracownikNaukowy.EgzaminyDyplomowe.PodgladListy')
    ) {
      this.router.navigateByUrl('/dyplomowanie/wykladowca/egzaminy', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.practices &&
      this.permissionsService.doesUserHavePermission('Praktyki.PracownikNaukowy.PraktykiStudentow.Lista')
    ) {
      this.router.navigateByUrl('/praktyki/wykladowca/praktyki-studentow', { replaceUrl: true });
    }

    if (
      url === baseModulesRoutes.classifieds &&
      this.permissionsService.doesUserHavePermission('Komunikacja.PlatformaPracownikaNaukowego.Ogloszenia.Lista')
    ) {
      this.router.navigateByUrl('/ogloszenia/wykladowca', { replaceUrl: true });
    }
    if (
      url === baseModulesRoutes.filesRepository &&
      this.permissionsService.doesUserHavePermission('RepozytoriumPlikow.PlatformaPracownikaNaukowego.Pliki.Lista')
    ) {
      this.router.navigateByUrl('/repozytorium-plikow/wykladowca/pliki-do-pobrania', { replaceUrl: true });
    }
  }
}
